"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.initDelay = void 0;
exports.initAnalytics = initAnalytics;
exports.trackEvent = trackEvent;
exports.setinitDelay = setinitDelay;
const courier_js_1 = __importDefault(require("@trycourier/courier-js"));
// TODO is this import forcing bundling of the whole library?
// If so, how can we avoid it?
const analytics_browser_1 = require("@amplitude/analytics-browser");
const function_helpers_1 = require("../helpers/function-helpers");
const appcues_helpers_1 = require("../helpers/appcues-helpers");
// Should constants and types be siblings of this file?
const INIT_DELAY_MS = 200;
const DEFAULT_TRACK_PRE_INIT_DELAY_MS = 10000; // Initing depends on fetching user and tracking props which could take a while
exports.initDelay = DEFAULT_TRACK_PRE_INIT_DELAY_MS;
const TRACKING_PLATFORM_NAMES = {
    COURIER: "courier",
    HOTJAR: "hotjar",
    APPCUES: "appcues",
    GTAG: "gtag",
    AMPLITUDE: "amplitude",
    CUSTOMER_IO: "customer.io",
};
let didInitAppcues = false;
let didInitGoogleAnalytics = false;
let didInitHotjar = false;
let didInitCourier = false;
let didInitAmplitude = false;
let didInitCustomer = false;
function initAnalytics(params) {
    const getConfigParams = {
        userId: params.userId,
        props: (0, appcues_helpers_1.trackingPropsToHash)(params.userTrackingProps),
        onError: (error) => params.onError(error),
    };
    const configs = getAnaliticsConfigs(getConfigParams);
    configs.forEach((config) => {
        (0, function_helpers_1.tryWithDelay)(config);
    });
}
function trackEvent({ eventName, eventParams, onError, }) {
    const configs = getEventTrackingConfigs({ eventName, eventParams, onError });
    configs.forEach((config) => {
        // If we're trying to track but not init yet, delay the firing.
        // Otherwise run right away
        const delayMsForTrack = config.shouldDelay ? exports.initDelay : 0;
        setTimeout(() => {
            try {
                config.functionToCall();
            }
            catch (error) {
                onTrackTrackError({
                    error: error,
                    onError,
                    eventName,
                    eventParams,
                    platformName: config.platformName,
                });
            }
        }, delayMsForTrack);
    });
}
// Helpers
function getAnaliticsConfigs(analyticsFnParams) {
    const maxAttempts = 10;
    const tryWithDelayShared = {
        delay: INIT_DELAY_MS,
        attemptNumber: 1,
        maxAttempts,
        hasSucceeded: false,
        captureException: analyticsFnParams.onError,
    };
    const msgPrefix = "Failed to initialize";
    const msgSuffix = `after ${maxAttempts} attempts`;
    return [
        Object.assign(Object.assign({}, tryWithDelayShared), { functionToCall: () => initAppcues(analyticsFnParams), errorMessage: `${msgPrefix} appcues ${msgSuffix}` }),
        Object.assign(Object.assign({}, tryWithDelayShared), { functionToCall: () => initGoogleAnalytics(analyticsFnParams), errorMessage: `${msgPrefix} GA ${msgSuffix}` }),
        Object.assign(Object.assign({}, tryWithDelayShared), { functionToCall: () => initHotjar(analyticsFnParams), errorMessage: `${msgPrefix} hotjar ${msgSuffix}` }),
        Object.assign(Object.assign({}, tryWithDelayShared), { functionToCall: () => initCourier(analyticsFnParams), errorMessage: `${msgPrefix} courier ${msgSuffix}` }),
        Object.assign(Object.assign({}, tryWithDelayShared), { functionToCall: () => initAmplitude(analyticsFnParams), errorMessage: `${msgPrefix} amplitude ${msgSuffix}` }),
        Object.assign(Object.assign({}, tryWithDelayShared), { functionToCall: () => initCustomerio(analyticsFnParams), errorMessage: `${msgPrefix} customer io ${msgSuffix}` }),
    ];
}
function initAppcues({ userId, props }) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!window.Appcues)
        return false;
    if (didInitAppcues)
        return true;
    // App cues
    window.Appcues.identify(userId, props);
    didInitAppcues = true;
    return true;
}
function initGoogleAnalytics({ userId, props }) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!window.gtag)
        return false;
    if (didInitGoogleAnalytics)
        return true;
    didInitGoogleAnalytics = true;
    // GA
    // https://developers.google.com/analytics/devguides/collection/ga4/reference/config#user_id
    window.gtag("set", "user_id", userId);
    window.gtag("set", "user_properties", props);
    return didInitGoogleAnalytics;
}
function initHotjar({ userId, props }) {
    if (!window.hj)
        return false;
    if (didInitHotjar)
        return true;
    didInitHotjar = true;
    window.hj("identify", userId, props);
    return didInitHotjar;
}
function initCourier({ userId, props }) {
    if (didInitCourier)
        return true;
    didInitCourier = true;
    courier_js_1.default.init({
        clientKey: process.env.GATSBY_COURIER_CLIENT_KEY,
    });
    // So we can use in courier automations
    const courierspecificProps = Object.assign({ userId }, props);
    void courier_js_1.default.identify(userId, courierspecificProps);
    return didInitCourier;
}
function initAmplitude({ userId, props }) {
    if (!window.amplitude)
        return false;
    if (didInitAmplitude)
        return true;
    didInitAmplitude = true;
    // Add session reply
    if (window.sessionReplay) {
        window.amplitude.add(window.sessionReplay.plugin({ sampleRate: 1 }));
    }
    // Init sdk with userId and options
    window.amplitude.init(process.env.GATSBY_AMPLITUDE_API_KEY, userId, {
        fetchRemoteConfig: true,
        autocapture: true,
    });
    // Track user props
    const identifyEvent = new analytics_browser_1.Identify();
    Object.entries(props).forEach(([key, value]) => {
        // look at ValidPropertyType for details
        if (isPrimitive(value)) {
            identifyEvent.set(key, value);
        }
    });
    window.amplitude.identify(identifyEvent, {
        user_id: userId,
    });
    return didInitAmplitude;
}
function initCustomerio({ userId, props }) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- make this ssr safe
    if (!window.analytics)
        return false;
    if (didInitCustomer)
        return true;
    didInitCustomer = true;
    void window.analytics.identify(userId, props);
    return didInitCustomer;
}
// Core event tracking
function getEventTrackingConfigs({ eventName, eventParams, onError, }) {
    // Tests rely on gtag to be first
    return [
        {
            functionToCall: () => window.gtag("event", eventName, eventParams),
            platformName: TRACKING_PLATFORM_NAMES.GTAG,
            shouldDelay: !didInitGoogleAnalytics,
        },
        {
            functionToCall: () => window.Appcues.track(eventName, eventParams),
            platformName: TRACKING_PLATFORM_NAMES.APPCUES,
            shouldDelay: !didInitAppcues,
        },
        {
            functionToCall: () => trackCourier({ eventName, eventParams, onError }),
            platformName: TRACKING_PLATFORM_NAMES.COURIER,
            shouldDelay: !didInitCourier,
        },
        {
            functionToCall: () => { var _a; return (_a = window.hj) === null || _a === void 0 ? void 0 : _a.call(window, "event", eventName); },
            platformName: TRACKING_PLATFORM_NAMES.HOTJAR,
            shouldDelay: !didInitHotjar,
        },
        {
            functionToCall: () => { var _a; return (_a = window.amplitude) === null || _a === void 0 ? void 0 : _a.track(eventName, eventParams); },
            platformName: TRACKING_PLATFORM_NAMES.AMPLITUDE,
            shouldDelay: !didInitAmplitude,
        },
        {
            functionToCall: () => {
                var _a;
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- importing their types sets `analytics` to a non null value but we're loading via script tag which is means we could have a null value
                void ((_a = window.analytics) === null || _a === void 0 ? void 0 : _a.track(eventName, eventParams));
            },
            platformName: TRACKING_PLATFORM_NAMES.CUSTOMER_IO,
            shouldDelay: !didInitCustomer,
        },
    ];
}
// We do thia so we can catch async errors easier
function trackCourier({ eventName, eventParams, onError, }) {
    void courier_js_1.default.track(eventName, eventParams).catch((error) => {
        onTrackTrackError({
            error: error,
            onError,
            eventName,
            eventParams,
            platformName: TRACKING_PLATFORM_NAMES.COURIER,
        });
    });
}
function onTrackTrackError({ error, onError, eventName, eventParams, platformName, }) {
    const hint = {
        data: {
            eventName,
            eventParams,
            platform: platformName,
        },
    };
    onError(error, hint);
}
// We do this so we can have a low delay for tests
function setinitDelay(delay) {
    exports.initDelay = delay;
}
function isPrimitive(value) {
    return typeof value !== "object" && typeof value !== "function";
}
